import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useToast } from '../../contexts/ToastContext.jsx';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import { ApiError } from '../../utils/apiClient.js';
import { parseApiError } from '../../utils/consolidatedErrorHandling.js';
import { apiRequest } from '../../utils/apiClient.js';

/**
 * Knowledge Base content editor component with preview functionality
 */
const KnowledgeBaseContentEditor = ({
  knowledgeBase,
  isOpen,
  onClose,
  onSave
}) => {
  const { getToken } = useAuth();
  const { showToast } = useToast();
  const { theme } = useTheme();
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [previewMode, setPreviewMode] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [replaceMode, setReplaceMode] = useState(false);
  const [replaceTerm, setReplaceTerm] = useState('');

  /**
   * Load Knowledge Base content
   */
  const loadContent = async () => {
    try {
      setLoading(true);
      const token = await getToken();
      
      const response = await apiRequest(`/api/admin/knowledge-base/${knowledgeBase.id}/content`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new ApiError(errorMessage, response.status, response);
      }

      const data = await response.json();
      const kbContent = data.content || '';
      setContent(kbContent);
      setOriginalContent(kbContent);
      setHasChanges(false);
    } catch (error) {
      console.error('Error loading content:', error);
      showToast('Failed to load content', 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle content change
   */
  const handleContentChange = (newContent) => {
    setContent(newContent);
    setHasChanges(newContent !== originalContent);
  };

  /**
   * Save content
   */
  const handleSave = async () => {
    try {
      setSaving(true);
      const token = await getToken();
      
      const response = await apiRequest(`/api/admin/knowledge-base/${knowledgeBase.id}/content`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content })
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new ApiError(errorMessage, response.status, response);
      }

      const updatedKB = await response.json();
      setOriginalContent(content);
      setHasChanges(false);
      showToast('Content saved successfully', 'success');
      
      if (onSave) {
        onSave(updatedKB);
      }
    } catch (error) {
      console.error('Error saving content:', error);
      showToast(error.message || 'Failed to save content', 'error');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Handle close with unsaved changes check
   */
  const handleClose = () => {
    if (hasChanges) {
      if (!confirm('You have unsaved changes. Are you sure you want to close?')) {
        return;
      }
    }
    onClose();
  };

  /**
   * Reset content to original
   */
  const handleReset = () => {
    if (confirm('Are you sure you want to reset all changes?')) {
      setContent(originalContent);
      setHasChanges(false);
    }
  };

  /**
   * Find and replace functionality
   */
  const handleFindReplace = () => {
    if (!searchTerm) {
      showToast('Please enter a search term', 'warning');
      return;
    }

    const regex = new RegExp(searchTerm, 'gi');
    const matches = content.match(regex);
    
    if (!matches) {
      showToast('No matches found', 'info');
      return;
    }

    if (replaceMode && replaceTerm !== undefined) {
      const newContent = content.replace(regex, replaceTerm);
      handleContentChange(newContent);
      showToast(`Replaced ${matches.length} occurrence${matches.length !== 1 ? 's' : ''}`, 'success');
    } else {
      showToast(`Found ${matches.length} match${matches.length !== 1 ? 'es' : ''}`, 'info');
    }
  };

  /**
   * Get content statistics
   */
  const getContentStats = () => {
    const words = content.trim().split(/\s+/).filter(word => word.length > 0).length;
    const characters = content.length;
    const lines = content.split('\n').length;
    const paragraphs = content.split(/\n\s*\n/).filter(p => p.trim().length > 0).length;
    
    return { words, characters, lines, paragraphs };
  };

  /**
   * Render content preview
   */
  const renderPreview = () => {
    // Simple markdown-like rendering
    let previewContent = content
      .replace(/^# (.*$)/gim, `<h1 class="text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2 dark:text-white">$1</h1>`)
      .replace(/^## (.*$)/gim, `<h2 class="text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2 dark:text-white">$1</h2>`)
      .replace(/^### (.*$)/gim, `<h3 class="text-md font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-1 dark:text-white">$1</h3>`)
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold">$1</strong>')
      .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>')
      .replace(/`(.*?)`/g, `<code class="${theme === 'dark' ? 'bg-gray-700 text-teal-400' : 'bg-gray-200 text-teal-600'} px-1 rounded">$1</code>`)
      .replace(/\n\n/g, `</p><p class="${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2 dark:text-gray-600">`)
      .replace(/\n/g, '<br>');
    
    previewContent = `<p class="${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2 dark:text-gray-600">` + previewContent + '</p>';
    
    return { __html: previewContent };
  };

  // Load content when component opens
  useEffect(() => {
    if (isOpen && knowledgeBase?.id) {
      loadContent();
    }
  }, [isOpen, knowledgeBase?.id]);

  if (!isOpen) return null;

  const stats = getContentStats();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`rounded-lg border w-full max-w-6xl max-h-[95vh] overflow-hidden ${
        theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        {/* Header */}
        <div className={`px-6 py-4 border-b flex justify-between items-center ${
          theme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'
        }`}>
          <div>
            <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Edit Knowledge Base Content</h3>
            <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              {knowledgeBase.name} • {stats.words} words, {stats.characters} characters
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {hasChanges && (
              <span className="text-yellow-400 text-sm">Unsaved changes</span>
            )}
            <button
              onClick={handleClose}
              className={`transition-colors ${
                theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className={`px-6 py-3 border-b flex items-center justify-between ${
          theme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'
        }`}>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setPreviewMode(false)}
                className={`px-3 py-1 text-sm rounded ${
                  !previewMode 
                    ? 'bg-teal-600 text-white' 
                    : theme === 'dark'
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Edit
              </button>
              <button
                onClick={() => setPreviewMode(true)}
                className={`px-3 py-1 text-sm rounded ${
                  previewMode 
                    ? 'bg-teal-600 text-white' 
                    : theme === 'dark'
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Preview
              </button>
            </div>

            {/* Find/Replace */}
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Find..."
                className={`px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-teal-500 ${
                  theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              />
              {replaceMode && (
                <input
                  type="text"
                  value={replaceTerm}
                  onChange={(e) => setReplaceTerm(e.target.value)}
                  placeholder="Replace with..."
                  className={`px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-teal-500 ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              )}
              <button
                onClick={() => setReplaceMode(!replaceMode)}
                className={`px-2 py-1 text-sm rounded ${
                  replaceMode 
                    ? 'bg-blue-600 text-white' 
                    : theme === 'dark'
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {replaceMode ? 'Replace' : 'Find'}
              </button>
              <button
                onClick={handleFindReplace}
                className="px-2 py-1 text-sm bg-teal-600 text-white rounded hover:bg-teal-700 dark:text-white"
              >
                Go
              </button>
            </div>
          </div>

          <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            {stats.lines} lines • {stats.paragraphs} paragraphs
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-hidden" style={{ height: 'calc(95vh - 200px)' }}>
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-400"></div>
              <span className={`ml-3 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Loading content...</span>
            </div>
          ) : previewMode ? (
            <div className="h-full overflow-y-auto p-6">
              <div 
                className="prose prose-invert max-w-none"
                dangerouslySetInnerHTML={renderPreview()}
              />
            </div>
          ) : (
            <textarea
              value={content}
              onChange={(e) => handleContentChange(e.target.value)}
              className={`w-full h-full p-6 font-mono text-sm resize-none focus:outline-none ${
                theme === 'dark' 
                  ? 'bg-gray-800 text-white' 
                  : 'bg-white text-gray-900'
              }`}
              placeholder="Enter your Knowledge Base content here..."
              spellCheck={false}
            />
          )}
        </div>

        {/* Footer */}
        <div className={`px-6 py-4 border-t flex justify-between items-center ${
          theme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'
        }`}>
          <div className="flex items-center space-x-4">
            <button
              onClick={handleReset}
              disabled={!hasChanges || saving}
              className={`px-3 py-1 text-sm rounded transition-colors disabled:opacity-50 ${
                theme === 'dark'
                  ? 'bg-gray-600 text-white hover:bg-gray-700'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Reset
            </button>
            <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              Last saved: {originalContent === content ? 'Up to date' : 'Unsaved changes'}
            </span>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={handleClose}
              disabled={saving}
              className={`px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${
                theme === 'dark'
                  ? 'bg-gray-600 text-white hover:bg-gray-700'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Close
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 dark:text-white"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeBaseContentEditor;
